import { useEffect, useRef } from 'react';
import normalizeLocationId from '../../utils/normalizeLocationId';
import type {
  BranchLocation,
  Location,
  LocationTreeWithRoot,
  NodeIndex,
} from '../../types';
import findAncestorsThatNeedExpanding from '../../utils/findAncestors';

interface UseAutoExpandAncestorsProps {
  tree: LocationTreeWithRoot;
  selectedLocations: Location[];
  nodeIndex: NodeIndex;
  onToggleOpen: (branch: BranchLocation) => void;
}

export const useAutoExpandAncestors = ({
  tree,
  selectedLocations,
  nodeIndex,
  onToggleOpen,
}: UseAutoExpandAncestorsProps) => {
  const expandedAncestorsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    selectedLocations.forEach((selectedLocation) => {
      const nodeEntry = nodeIndex[normalizeLocationId(selectedLocation.id)];

      if (!nodeEntry) {
        const ancestorsToExpand = findAncestorsThatNeedExpanding(
          tree,
          selectedLocation
        );

        ancestorsToExpand.forEach((ancestor) => {
          if (
            !ancestor.isOpen &&
            !expandedAncestorsRef.current.has(normalizeLocationId(ancestor.id))
          ) {
            expandedAncestorsRef.current.add(normalizeLocationId(ancestor.id));
            onToggleOpen(ancestor);
          }
        });
      }
    });

    // Reset the ref when all selections are found in the tree
    if (
      selectedLocations.every((loc) => nodeIndex[normalizeLocationId(loc.id)])
    ) {
      expandedAncestorsRef.current.clear();
    }
  }, [selectedLocations, tree, nodeIndex, onToggleOpen]);
};

export default useAutoExpandAncestors;
